/**
 * Widget Defaults
 * Default objects for widget customization state.
 */

import type {
  WidgetThemeStyles,
  WidgetPlacementConfig,
  WidgetFeatureConfig,
  WidgetTemplateFilters,
  WidgetIconConfig,
  WidgetBadgeConfig,
  WidgetAnimationConfig,
  WidgetResponsiveConfig,
  WidgetFloatingBadgeConfig,
} from "../types";

import type { WidgetBrandConfig, WidgetBehaviorConfig } from "../types";

// ============================================
// Default Theme Styles
// ============================================

export const DEFAULT_THEME_STYLES: WidgetThemeStyles = {
  primaryColor: "#25D366",
  secondaryColor: "#128C7E",
  backgroundColor: "#FFFFFF",
  textColor: "#1F2937",

  hoverColor: "#1EBE5A",
  badgeColor: "#FF4D4F",
  statusOnlineColor: "#52C41A",
  statusOfflineColor: "#8C8C8C",
  borderColor: "#E5E7EB",
  shadowColor: "rgba(0,0,0,0.12)",

  iconColor: "#FFFFFF",
  closeIconColor: "#FFFFFF",
  closeIconBackgroundColor: "transparent",

  hoverBackgroundColor: "#1EBE5A",
  hoverIconColor: "#FFFFFF",

  iconType: "whatsapp",
  iconShape: "circle",
  iconAnimation: "none",
  closeIconType: "x",
  closeIconSize: 20,

  buttonShape: "circle",
  borderRadius: 16,
  shadowStyle: "medium",
  borderStyle: "none",
  borderWidth: 0,
  iconSize: 28,
  iconPadding: 14,

  fontFamily: '"Readex Pro", system-ui, sans-serif',
  fontWeight: 500,
  fontSize: 14,
  lineHeight: 1.5,
  textCase: "none",

  buttonStyle: "solid",

  useGradient: false,
  gradientDirection: "135deg",
  gradientType: "linear",

  headerColor: "#25D366",

  enableDarkMode: false,
  darkModeColors: {
    primaryColor: "#25D366",
    secondaryColor: "#128C7E",
    backgroundColor: "#1F2937",
    textColor: "#F9FAFB",
    hoverColor: "#1EBE5A",
    badgeColor: "#FF4D4F",
    statusOnlineColor: "#52C41A",
    statusOfflineColor: "#6B7280",
    borderColor: "#374151",
    shadowColor: "rgba(0,0,0,0.3)",
  },
};

// ============================================
// Default Responsive Config
// ============================================

export const DEFAULT_RESPONSIVE_CONFIG: WidgetResponsiveConfig = {
  desktop: {
    enabled: true,
    position: "bottom-right",
    offsetX: 0,
    offsetY: 0,
    scale: 1,
  },
  mobile: {
    enabled: true,
    position: "bottom-right",
    offsetX: 0,
    offsetY: 0,
    scale: 0.9,
    useStickyFooter: false,
  },
  tablet: {
    enabled: true,
    position: "bottom-right",
    offsetX: 0,
    offsetY: 0,
    scale: 1,
  },
  hideOnScreenSizes: [],
  safeAreaHandling: true,
};

// ============================================
// Default Placement Config
// ============================================

export const DEFAULT_PLACEMENT_CONFIG: WidgetPlacementConfig = {
  type: "bottom-right",
  marginTop: 0,
  marginBottom: 24,
  marginLeft: 0,
  marginRight: 24,
  offsetX: 0,
  offsetY: 0,
  animationType: "slide",
  animationDuration: 300,
  zIndex: 999999,
  containerSelector: undefined,
  responsive: DEFAULT_RESPONSIVE_CONFIG,
};

// ============================================
// Default Feature Config
// ============================================

export const DEFAULT_FEATURE_CONFIG: WidgetFeatureConfig = {
  type: "chat-preview",
  buttonText: "Chat with Us",
  showChatHistory: true,
  previewMessageCount: 1,
};

// ============================================
// Default Brand Config
// ============================================

export const DEFAULT_BRAND_CONFIG: WidgetBrandConfig = {
  brandName: "Notifyy",
  brandSubtitle: "Typically replies within minutes",
  brandColor: "#25D366",
  brandImageURL: "",
  phoneNumber: "",
  defaultMessage: "Hello! I would like to know more about your services.",
  widgetCTAText: "Start Chat",
  agentName: "Support",
  agentAvatar: "",
  onlineStatusText: "Online now",
  offlineStatusText: "We will reply soon",
  welcomeMessage: "Hi there! How can we help you today?",
};

// ============================================
// Default Icon Config
// ============================================

export const DEFAULT_ICON_CONFIG: WidgetIconConfig = {
  type: "whatsapp",
  customSvg: undefined,
  emoji: undefined,
  animation: "none",
  color: "#FFFFFF",
  size: 28,
  shape: "circle",
  closeIconType: "x",
  closeIconColor: "#FFFFFF",
  closeIconSize: 20,
};

// ============================================
// Default Badge Config
// ============================================

export const DEFAULT_BADGE_CONFIG: WidgetBadgeConfig = {
  type: "count",
  text: "1",
  backgroundColor: "#FF4D4F",
  textColor: "#FFFFFF",
  position: "top-right",
  animated: true,
};

// ============================================
// Default Animation Config
// ============================================

export const DEFAULT_ANIMATION_CONFIG: WidgetAnimationConfig = {
  type: "slide",
  duration: 300,
  delay: 0,
  easing: "ease-out",
  enablePulse: false,
  pulseColor: "rgba(37, 211, 102, 0.4)",
  pulseScale: 1.2,
  enableNudge: false,
  nudgeDelay: 30,
  nudgeType: "none",
  respectReducedMotion: true,
};

// ============================================
// Default Floating Badge Config
// ============================================

export const DEFAULT_FLOATING_BADGE_CONFIG: WidgetFloatingBadgeConfig = {
  enabled: true,
  message: "How can we help you?",
  backgroundColor: "#FFFFFF",
  textColor: "#1F2937",
  fontSize: 14,
  glossyEffect: true,
  borderColor: "#E5E7EB",
  borderWidth: 1,
  position: "left",
  maxWidth: 200,
  textAlign: "left",
};

// ============================================
// Default Behavior Config
// ============================================

export const DEFAULT_BEHAVIOR_CONFIG: WidgetBehaviorConfig = {
  openByDefault: false,
  reopenSessionWindow: "ONCE",
  showBadge: true,
  badgeText: "1",
  floatingBadge: DEFAULT_FLOATING_BADGE_CONFIG,
};

// ============================================
// Default Template Filters
// ============================================

export const DEFAULT_TEMPLATE_FILTERS: WidgetTemplateFilters = {
  theme: "all",
  placement: "all",
  featureType: "all",
  category: "all",
  search: "",
  isPremium: null,
  sortBy: "popular",
};

